import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { Button, Space, Tooltip, Input, Divider, Dropdown } from 'antd';
import {
  FileSearchOutlined,
  LogoutOutlined,
  LoginOutlined,
  InboxOutlined,
  FolderOpenOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { FiExternalLink } from 'react-icons/fi';
import RoleChecker from '@components/RoleChecker';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dateFormat, getDateInISOFormat } from '@utils/formatters';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import 'moment/locale/pt-br';
import SessionContext from '@providers/Session/SessionContext';
import moment from 'moment';
import { IObject } from '@utils/defaultTypes';
import SelectCustomers from '@components/SelectCustomers';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import useSearchParams from '@hooks/useSearchParams';
import rolesConstants from '@utils/rolesConstants';
import ActionsModalByTypes from '@components/Actions/ActionsModal/ActionsModalByTypes';
import { ApplicationsConstants } from '@utils/applicationsConstants';

const { Search } = Input;

interface ITransfers {
  isInTheRequestDetails?: boolean;
  defaultFilters?: IObject;
  columnsToRemove?: string[];
}

const Transfers: React.FC<ITransfers> = ({
  isInTheRequestDetails,
  defaultFilters,
  columnsToRemove,
}) => {
  const { t } = useTranslation(['transfers', 'inventory']);
  const { isRoot } = useContext(SessionContext);
  const {
    isMotBusiness,
    hasCustomersRoles,
    customer: { customerLogged, isCustomerFinanciallyBlocked },
    selectedApp,
    theme,
    hasMotLorawan,
  } = useContext(SessionContext);

  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [msisdnValue, setMsisdnValue] = useState('');
  const [iccidValue, setIccidValue] = useState('');
  const [imsiValue, setImsiValue] = useState('');
  const [transferredByValue, setTransferredByValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['status', 'virtualAccountId', 'archived'],
    extraParams: [
      'customerId',
      'startDate',
      'endDate',
      'msisdn',
      'iccid',
      'imsi',
      'transferredBy',
    ],
  });

  const {
    customerId,
    startDate,
    endDate,
    msisdn,
    iccid,
    imsi,
    status,
    transferredBy,
    virtualAccountId,
    archived,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const isArchived = archived === '1';

  const filters = useMemo(() => {
    const allFilters = {
      archived,
      customerId,
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      startDate,
      endDate,
      status,
      imsi,
      msisdn,
      iccid,
      username: transferredBy,
      virtualAccountId,
      ...defaultFilters,
    };

    return allFilters;
  }, [
    page,
    linesPerPage,
    startDate,
    endDate,
    status,
    imsi,
    msisdn,
    iccid,
    transferredBy,
    archived,
    customerId,
    defaultFilters,
    virtualAccountId,
  ]);

  const { data } = useSwr(
    '/service-proxy/sims/simcard-transfer',
    {
      params: filters,
    },
    {
      refreshInterval: 10 * 1000,
    },
  );

  const { data: countByStatus } = useSwr(
    '/service-proxy/sims/simcard-transfer-count-by-status',
    {
      params: {
        // @ts-ignore
        archived,
        customerId,
        startDate,
        endDate,
        status: status?.toUpperCase(),
        imsi,
        msisdn,
        iccid,
        username: transferredBy,
        virtualAccountId,
        ...defaultFilters,
      },
    },
    { refreshInterval: 10 * 1000 },
  );

  useEffect(() => {
    setMsisdnValue(msisdn);
  }, [msisdn]);

  useEffect(() => {
    setIccidValue(iccid);
  }, [iccid]);

  useEffect(() => {
    setImsiValue(imsi);
  }, [imsi]);

  useEffect(() => {
    setTransferredByValue(transferredBy);
  }, [transferredBy]);

  const fromToButton = useCallback(
    (record: any) => {
      const currentVAId = defaultFilters?.virtualAccountId;
      const oldVAId = record.oldCustomerVirtualAccountId;
      const newVAId = record.newCustomerVirtualAccountId;
      const isOldVAId = currentVAId === oldVAId;
      const isOldVAIdNull = oldVAId === null;
      const isNewVAIdNull = newVAId === null;
      const isOldVAIdZero = oldVAId === 0;
      const isNewVAIdZero = newVAId === 0;
      const isOldBrokerStock = record.oldCustomer === customerLogged?.id;
      const isNewBrokerStock = record.newCustomer === customerLogged?.id;

      let tooltipText = t(
        t(`movement.see${isOldVAId ? 'Destination' : 'Origin'}Request`),
      );
      let icon = isOldVAId ? <LogoutOutlined /> : <LoginOutlined />;
      const stockIcon = <InboxOutlined style={{ fontSize: '1.25rem' }} />;
      let idToLink: number;
      if (isOldVAIdNull || isOldBrokerStock || isOldVAIdZero) {
        tooltipText = t('movement.transferred', {
          option: t('movement.fromStock'),
        });
        icon = stockIcon;
      } else if (isNewVAIdNull || isNewBrokerStock || isNewVAIdZero) {
        tooltipText = t('movement.transferred', {
          option: t('movement.forStock'),
        });
        icon = stockIcon;
      } else if (isOldVAId) {
        idToLink = newVAId;
      } else {
        idToLink = oldVAId;
      }

      const showButton =
        !isOldVAIdNull &&
        !isNewVAIdNull &&
        !isOldBrokerStock &&
        !isNewBrokerStock &&
        !isOldVAIdZero &&
        !isNewVAIdZero;

      return (
        <Dropdown
          overlayStyle={{
            background: 'white',
            padding: '4px 8px',
            borderRadius: 5,
            boxShadow: '4px 8px 30px -4px rgb(0 51 90 / 10%)',
          }}
          overlay={
            <div
              style={{
                color: '#333',
                padding: '2px 6px',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              {showButton ? (
                <Button
                  type="link"
                  danger={isNewVAIdNull || isOldVAId || isNewVAIdZero}
                  onClick={() => {
                    navigate(
                      `/${selectedApp}/${
                        hasMotLorawan ? 'mobile-network/' : ''
                      }financial/requests/${idToLink}`,
                    );
                  }}
                >
                  {tooltipText}
                </Button>
              ) : (
                tooltipText
              )}
            </div>
          }
        >
          <Button
            icon={icon}
            danger={isNewVAIdNull || isOldVAId || isNewVAIdZero}
          />
        </Dropdown>
      );
    },
    [
      defaultFilters?.virtualAccountId,
      customerLogged?.id,
      t,
      navigate,
      selectedApp,
      hasMotLorawan,
    ],
  );

  const columnGrouping = useCallback(
    (_: any, record: any) =>
      record.newCustomerVirtualAccountId ? (
        <Tooltip title={t('inventory:view-grouping')}>
          <Button
            icon={<FileSearchOutlined />}
            onClick={() => {
              navigate(
                `/${ApplicationsConstants.MOT_BUSINESS}/${
                  hasMotLorawan ? 'mobile-network/' : ''
                }administrative/groupings/${
                  record.newCustomerVirtualAccountId
                }`,
              );
            }}
          />
        </Tooltip>
      ) : undefined,
    [hasMotLorawan, navigate, t],
  );

  const columnRequest = useCallback(
    (_: any, record: any) =>
      record.newCustomerVirtualAccountId ? (
        <Tooltip title={t('table.seeRequest')}>
          <Button
            icon={<FileSearchOutlined />}
            onClick={() => {
              navigate(
                `/${selectedApp}/${
                  hasMotLorawan ? 'mobile-network/' : ''
                }financial/requests/${record.newCustomerVirtualAccountId}`,
              );
            }}
          />
        </Tooltip>
      ) : undefined,
    [hasMotLorawan, navigate, selectedApp, t],
  );

  const columns = useMemo(() => {
    const allColumns = [
      !isInTheRequestDetails
        ? {
            title: t('table.from'),
            dataIndex: 'oldCustomer',
            render: (value: IObject) =>
              value?.name || value?.tradingName || value?.nickname,
          }
        : undefined,
      !isInTheRequestDetails
        ? {
            title: t('table.to'),
            dataIndex: 'newCustomer',
            render: (value: IObject) =>
              value?.name || value?.tradingName || value?.nickname,
          }
        : undefined,
      isInTheRequestDetails
        ? {
            title: t('movement.title'),
            dataIndex: 'transfersCustomers',
            align: 'center',
            render: (_: any, record: IObject) => fromToButton(record),
          }
        : undefined,
      { title: t('table.date'), dataIndex: 'changeDate', render: dateFormat },
      { title: t('table.msisdn'), dataIndex: 'msisdn' },
      { title: t('table.iccid'), dataIndex: 'iccid' },
      { title: t('table.imsi'), dataIndex: 'imsi' },
      { title: t('table.transferred-by'), dataIndex: 'username' },
      isMotBusiness
        ? {
            title: t('table.grouping.title'),
            align: 'center',
            dataIndex: 'request',
            render: columnGrouping,
          }
        : {
            title: t('table.request'),
            align: 'center',
            dataIndex: 'request',
            render: columnRequest,
          },
      {
        title: t('table.result.title'),
        dataIndex: 'result',
        render: (value: IObject) =>
          t(`table.result.options.${value}`, { type: 'simcard' }),
      },
    ].filter(col => col);
    if (columnsToRemove) {
      return allColumns.filter(
        // @ts-ignore
        column => !columnsToRemove.includes(column.dataIndex),
      );
    }
    return allColumns;
  }, [
    isInTheRequestDetails,
    t,
    isMotBusiness,
    columnGrouping,
    columnRequest,
    columnsToRemove,
    fromToButton,
  ]);

  const getColorStatus = useCallback(
    (statusName: string) => {
      if (statusName === 'SUCCESS') {
        return theme?.colors?.green;
      }
      return theme?.colors?.red;
    },
    [theme?.colors?.green, theme?.colors?.red],
  );

  const statusMenuData = useMemo(() => {
    const statusData = countByStatus
      // @ts-ignore
      ?.map(({ item, value }) => ({
        label: t(`table.result.options.${item}`, { type: 'simcard' }),
        value: item,
        total: value,
        color: getColorStatus(item),
      }))
      // @ts-ignore
      ?.sort((a, b) => a.value - b.value);
    return statusData?.reverse();
  }, [countByStatus, t, getColorStatus]);

  const handleDateChange = useCallback(
    (values: any) => {
      const start = getDateInISOFormat(values.startDate);
      const end = getDateInISOFormat(values.endDate);
      setSearchParams({ startDate: start, endDate: end });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('dateRange'),
        element: (
          <QuickRangePicker
            onChange={handleDateChange}
            defaultValue={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            style={{ width: '100%' }}
          />
        ),
      },
      {
        visible: hasCustomersRoles && !isInTheRequestDetails,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: !isRoot && isMotBusiness ? t('department') : t('client'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
            isMotBusiness={!isRoot && isMotBusiness}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.msisdn'),
        element: (
          <Search
            onSearch={value => setSearchParams({ msisdn: value })}
            onChange={({ target: { value } }) => setMsisdnValue(value)}
            value={msisdnValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.iccid'),
        element: (
          <Search
            onSearch={value => setSearchParams({ iccid: value })}
            onChange={({ target: { value } }) => setIccidValue(value)}
            value={iccidValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.imsi'),
        element: (
          <Search
            onSearch={value => setSearchParams({ imsi: value })}
            onChange={({ target: { value } }) => setImsiValue(value)}
            value={imsiValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.transferred-by'),
        element: (
          <Search
            onSearch={value => setSearchParams({ transferredBy: value })}
            onChange={({ target: { value } }) => setTransferredByValue(value)}
            value={transferredByValue}
            allowClear
          />
        ),
      },
    ],
    [
      t,
      handleDateChange,
      startDate,
      endDate,
      hasCustomersRoles,
      isInTheRequestDetails,
      customerId,
      msisdnValue,
      iccidValue,
      imsiValue,
      transferredByValue,
      setSearchParams,
      isMotBusiness,
      isRoot,
    ],
  );

  const extraActions = useMemo(
    () =>
      !isCustomerFinanciallyBlocked &&
      !isInTheRequestDetails && (
        <Space>
          <RoleChecker role={rolesConstants.TRANSFER}>
            <Button
              type="primary"
              onClick={() => setShowModal(true)}
              style={{ marginRight: 12 }}
            >
              {t('batch-transfer-button')}
            </Button>
          </RoleChecker>
        </Space>
      ),
    [isCustomerFinanciallyBlocked, isInTheRequestDetails, t],
  );

  return (
    <>
      <Table
        // @ts-ignore
        columns={columns}
        data={data?.content}
        extraActions={extraActions}
        loading={!data?.content}
        tableKey={isInTheRequestDetails ? 'transferInDetails' : 'transfer'}
        title={
          <>
            {t('title')}
            <Tooltip title={t(`${isArchived ? 'hide' : 'show'}Archived`)}>
              <Button
                style={{ marginLeft: 8 }}
                type={isArchived ? 'primary' : 'default'}
                icon={isArchived ? <FolderOpenOutlined /> : <FolderOutlined />}
                onClick={() => {
                  setSearchParams({
                    archived: isArchived ? '0' : '1',
                  });
                }}
              />
            </Tooltip>
          </>
        }
        hasFilters
        statusMenu={{
          open: statusMenuData?.length > 0,
          itemActive: status,
          list: statusMenuData,
          onChange: item => {
            setSearchParams({
              status: item.value !== status ? item.value : undefined,
            });
          },
          showIconClear: true,
        }}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        mainExtraAction={
          isInTheRequestDetails && (
            <>
              <Divider type="vertical" style={{ marginLeft: 16 }} />
              <Tooltip title={t('requests:viewOnTransferPage')}>
                <Button
                  icon={
                    <FiExternalLink
                      style={{
                        marginTop: 4,
                      }}
                    />
                  }
                  onClick={() => {
                    navigate(
                      `/${selectedApp}/${
                        hasMotLorawan ? 'mobile-network/' : ''
                      }operational/transfers?virtualAccountId=${
                        defaultFilters?.virtualAccountId
                      }`,
                    );
                  }}
                />
              </Tooltip>
            </>
          )
        }
      />

      {!isCustomerFinanciallyBlocked && (
        <ActionsModalByTypes.Simcard
          visible={showModal}
          onClose={() => setShowModal(false)}
          actionRole={rolesConstants.TRANSFER}
          selectedDevices={[]}
          isUpload
        />
      )}
    </>
  );
};

export default Transfers;
